/**
 * This component represents a single Segment Control that is part of a group of
 * Segment Controls.  This control is a heavily styled Radio input button.  The
 * radio button itself is hidden.  The click event on its coresponding `label` is
 * what causes the selection to occur.
 *
 * Figma:
 * https://www.figma.com/file/QtodvbP7iasbakEq4bWcpwWJ/LIHP-MAP-updates?node-id=2486%3A0
 *
 * NOTE(erik): Use ./page-tabs.jsx if you need a navigation tabs that link to
 * other URLs.  This component is not accessible for navigation links
 */

import {StyleSheet} from "aphrodite";
import * as React from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import {View} from "@khanacademy/wonder-blocks-core";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";

type Props = {
    /**
     * Optional ID for the `aria-live` section that this Segmented Control
     * controls.
     */
    ariaControlsId?: string;
    /** Indicates the order which it appears.  Used to create a unique ID. */
    index: number;
    /** Used to determine whether or not to show a `border-right` on label. */
    isLast: boolean;
    /** Unique name for button. */
    name: string;
    /**
     * Method to call when the button is clicked.  It's passed the value of
     * the button.
     */
    onChange: (arg1: string) => void;
    /** Determines whether the button is active. */
    checked: boolean;
    /** Copy to display in the button's Label. */
    text: string;
    /** Value of the button. Passed to `onChange`. */
    value: string;
};

function SegmentedControl(props: Props): React.ReactElement {
    const {ariaControlsId, checked, index, isLast, name, text, value} = props;
    const id = `${name}${index}`;
    const labelId = `${id}-label`;

    return (
        <Clickable
            id={id}
            onClick={() => props.onChange(value)}
            aria-selected={checked ? "true" : "false"}
            role="tab"
        >
            {({hovered, focused, pressed}) => (
                <View
                    style={[
                        styles.button,
                        isLast && styles.noBorderRight,
                        hovered && styles.hovered,
                        checked && styles.pressed,
                    ]}
                >
                    <LabelMedium
                        aria-controls={ariaControlsId}
                        style={styles.label}
                        id={labelId}
                    >
                        {text}
                    </LabelMedium>
                </View>
            )}
        </Clickable>
    );
}

const borderWidth = "4px";
const styles = StyleSheet.create({
    button: {
        alignItems: "stretch",
        background: "none",
        borderBottom: `${borderWidth} solid transparent`,
        borderRight: `1px solid ${color.offBlack16}`,
        textAlign: "center",
        minWidth: 76,
    },
    hovered: {
        cursor: "pointer",
    },
    pressed: {
        borderBottom: `${borderWidth} solid ${color.blue}`,
        color: color.blue,
    },
    label: {
        display: "block",
        fontFamily: "Lato",
        fontSize: 16,
        fontWeight: 400,
        lineHeight: "20px",
        padding: `8px 16px ${borderWidth}`,
    },
    noBorderRight: {
        borderRight: "none",
    },
});

export default SegmentedControl;
